import {
  BarChart,
  Brain,
  Code,
  Database,
  Eye,
  EyeOff,
  Globe,
  HardDrive,
  Info,
  Layers,
  Server,
  Users,
} from "lucide-react";
import { useState } from "react";
import { InputParam } from "../../../types/service-catalog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Separator } from "../../ui/separator";
import { BundledServicesList } from "./BundledServicesList";
import { GitRepoSelector } from "./GitRepoSelector";
import { ServiceConfigurationFormProps } from "./service-creation-types";

const categoryIconMap: Record<string, React.ComponentType<any>> = {
  "web-applications": Globe,
  databases: Database,
  development: Code,
  infrastructure: Server,
  "ai-ml": Brain,
  applications: Layers,
  devops: Layers,
  collaboration: Users,
  analytics: BarChart,
  "object-storage": HardDrive,
};

const categoryColorMap: Record<string, string> = {
  databases: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
  cache: "bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400",
  search: "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400",
  "object-storage": "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400",
  "web-applications": "bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400",
  development: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  "ai-ml": "bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400",
  analytics: "bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400",
  devops: "bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400",
  infrastructure: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
};

const STORAGE_OPTIONS = ["512Mi", "1Gi", "2Gi", "5Gi", "10Gi", "20Gi", "50Gi"];

// ── Field renderer ────────────────────────────────────────────────────────────

interface FieldProps {
  param: InputParam;
  value: string;
  onChange: (val: string) => void;
}

function DynamicField({ param, value, onChange }: FieldProps) {
  const [show, setShow] = useState(false);
  const currentValue = value !== "" ? value : (param.default ?? "");

  switch (param.type) {
    case "password":
      return (
        <div className="relative">
          <Input
            id={param.key}
            type={show ? "text" : "password"}
            placeholder={param.placeholder || `Enter ${param.label.toLowerCase()}`}
            value={currentValue}
            onChange={(e) => onChange(e.target.value)}
            className="pr-10"
          />
          <button
            type="button"
            onClick={() => setShow((p) => !p)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label={show ? "Hide" : "Show"}
          >
            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      );

    case "storage":
      return (
        <Select value={currentValue || "1Gi"} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select size" />
          </SelectTrigger>
          <SelectContent>
            {STORAGE_OPTIONS.map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );

    case "select":
      return (
        <Select value={currentValue || param.default || ""} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue placeholder={`Select ${param.label.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent>
            {(param.options || []).map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );

    case "number":
      return (
        <Input
          id={param.key}
          type="number"
          placeholder={param.placeholder || param.default}
          value={currentValue}
          onChange={(e) => onChange(e.target.value)}
          min={param.min}
          max={param.max}
        />
      );

    case "bool":
      return (
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={currentValue === "true"}
            onChange={(e) => onChange(e.target.checked ? "true" : "false")}
            className="h-4 w-4 rounded border-input"
          />
          <span className="text-sm text-muted-foreground">{param.label}</span>
        </label>
      );

    default: // text
      return (
        <Input
          id={param.key}
          type="text"
          placeholder={param.placeholder || `Enter ${param.label.toLowerCase()}`}
          value={currentValue}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}

// ── Main component ────────────────────────────────────────────────────────────

export function ServiceConfigurationForm({
  selectedTemplate,
  serviceData,
  onServiceDataChange,
  onBack,
  onContinue,
  onCreate,
  isCreating,
  embedded = false,
}: ServiceConfigurationFormProps) {
  if (!selectedTemplate) return null;

  const requiresRepo = selectedTemplate.defaultConfig?.requiresRepository ?? true;
  const isContainerized = ["containerized", "dockerfile"].includes(
    selectedTemplate.type ?? ""
  );
  const allInputParams: InputParam[] = selectedTemplate.defaultConfig?.inputParams ?? [];

  // Filter out env-vars type params (handled in the dedicated env vars step) and
  // the legacy "port" key for containerized services (platform defaults to 3000).
  const inputParams = allInputParams.filter(
    (p) => p.type !== "env-vars" && p.key !== "port"
  );

  const updateServiceData = (updates: Partial<typeof serviceData>) => {
    onServiceDataChange({ ...serviceData, ...updates });
  };

  const updateRepository = (repoUpdates: Partial<typeof serviceData.repository>) => {
    updateServiceData({ repository: { ...serviceData.repository, ...repoUpdates } });
  };

  const updateParam = (key: string, value: string) => {
    updateServiceData({
      customCredentials: { ...serviceData.customCredentials, [key]: value },
    });
  };

  // Group params by their group label (empty string = no header)
  const groupedParams: Array<{ group: string; params: InputParam[] }> = [];
  const groups = new Map<string, InputParam[]>();
  for (const p of inputParams) {
    const g = p.group || "";
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g)!.push(p);
  }
  for (const [group, params] of Array.from(groups)) {
    groupedParams.push({ group, params });
  }

  const IconComponent = categoryIconMap[selectedTemplate.category] || Server;
  const iconColor = categoryColorMap[selectedTemplate.category] || "bg-muted text-muted-foreground";

  return (
    <div className={embedded ? "flex flex-col h-full gap-6" : "space-y-6"}>
      {/* Header — hidden when embedded (parent page shows service identity in sidebar) */}
      {!embedded && (
        <>
          <div className="flex items-center gap-3 pb-2">
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${iconColor}`}>
              <IconComponent className="h-5 w-5" />
            </div>
            <div>
              <p className="font-semibold leading-tight">
                {selectedTemplate.displayName || selectedTemplate.name}
              </p>
              {selectedTemplate.description && (
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                  {selectedTemplate.description}
                </p>
              )}
            </div>
          </div>
          <Separator />
        </>
      )}

      {/* Service name */}
      <div className={`space-y-2${embedded ? " shrink-0" : ""}`}>
        <Label htmlFor="service-name">
          Service Name <span className="text-destructive">*</span>
        </Label>
        <Input
          id="service-name"
          placeholder="my-awesome-service"
          value={serviceData.name}
          onChange={(e) => {
            const sanitized = e.target.value
              .toLowerCase()
              .replace(/[^a-z0-9-]/g, "-")
              .replace(/-{3,}/g, "--")
              .replace(/^-+/, "")
              .slice(0, 40);
            updateServiceData({ name: sanitized });
          }}
        />
        <p className="text-xs text-muted-foreground">
          Lowercase letters, numbers, and hyphens only.
        </p>
      </div>

      {/* Repository (web services only) */}
      {requiresRepo && (
        <div className={embedded ? "flex-1 min-h-0 overflow-hidden" : undefined}>
          <GitRepoSelector
            value={serviceData.repository}
            onChange={(repo) => updateRepository(repo)}
            fillHeight={embedded}
          />
        </div>
      )}

      {/* Root Directory — shown only when a repo is connected (monorepo support) */}
      {requiresRepo && serviceData.repository.url && (
        <div className="space-y-1.5">
          <Label htmlFor="root-directory">Root Directory</Label>
          <Input
            id="root-directory"
            placeholder="apps/frontend"
            value={serviceData.repository.rootDirectory ?? ""}
            onChange={(e) =>
              updateRepository({ rootDirectory: e.target.value || undefined })
            }
          />
          <p className="text-xs text-muted-foreground">
            Subdirectory to build from. Leave empty if your service is at the repo root.
          </p>
        </div>
      )}

      {/* Dockerfile required warning — shown for containerized/dockerfile types once a repo is connected */}
      {isContainerized && serviceData.repository.url && (
        <div className="flex gap-2.5 rounded-md bg-warning-bg p-3 text-sm text-warning-fg">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="space-y-1">
            <p className="font-medium">Dockerfile required</p>
            <p className="text-xs opacity-80">
              A <code className="font-mono">Dockerfile</code> must exist at the root of your
              {serviceData.repository.rootDirectory
                ? ` "${serviceData.repository.rootDirectory}" directory`
                : " repository"}
              . Without it the platform cannot build your container image.
            </p>
          </div>
        </div>
      )}

      {/* Dynamic configuration from driver InputParams (credentials, storage, etc.) */}
      {inputParams.length > 0 && (
        <div className={`space-y-4${embedded ? " shrink-0" : ""}`}>
          <div>
            <p className="text-sm font-medium">Configuration</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Leave password fields blank to auto-generate a secure password.
            </p>
          </div>

          {groupedParams.map(({ group, params }) => (
            <div key={group} className="space-y-3">
              {group && (
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b pb-1">
                  {group}
                </p>
              )}
              <div className="grid grid-cols-2 gap-4">
                {params.map((param) => (
                  <div
                    key={param.key}
                    className={`space-y-1.5 ${param.type === "bool" ? "flex items-center pt-6" : ""}`}
                  >
                    {param.type !== "bool" && (
                      <Label htmlFor={param.key}>
                        {param.label}
                        {param.required && <span className="text-destructive ml-1">*</span>}
                      </Label>
                    )}
                    {param.description && (
                      <p className="text-2xs text-muted-foreground">{param.description}</p>
                    )}
                    <DynamicField
                      param={param}
                      value={serviceData.customCredentials?.[param.key] ?? ""}
                      onChange={(val) => updateParam(param.key, val)}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bundled services — shown when the catalog item includes co-deployed services */}
      {(selectedTemplate.bundledServices?.length ?? 0) > 0 && (
        <BundledServicesList bundledServices={selectedTemplate.bundledServices} />
      )}

      {/* Footer — hidden when embedded (parent page owns the pinned footer) */}
      {!embedded && (
        <>
          <Separator />
          <div className="flex justify-between items-center">
            <Button variant="outline" onClick={onBack}>← Back</Button>
            {onContinue ? (
              <Button onClick={onContinue}>Continue →</Button>
            ) : (
              <Button onClick={onCreate} disabled={isCreating}>
                {isCreating && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                )}
                {isCreating ? "Deploying…" : `Deploy ${selectedTemplate.displayName}`}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
